import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { getRecaptchaToken, RECAPTCHA_ENABLED } from "@/lib/recaptcha";
import { buildAttribution } from "@/lib/attribution";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback source_channel derived from the current route when a page doesn't pass one
const inferSourceChannel = () => {
  const p = typeof window !== "undefined" ? window.location.pathname : "/";
  if (p === "/") return "homepage";
  if (p.startsWith("/search")) return "search";
  if (p.startsWith("/finance/")) return "finance_page";
  if (p.startsWith("/locations/") || p.startsWith("/finance-broker/")) return "location_page";
  if (p.startsWith("/about")) return "about_page";
  if (p.startsWith("/insights")) return "insights";
  return "form";
};

const LOAN_TYPES = [
  // Primary specialisms — matched exactly to the Landing loan-type card titles
  // so clicking a card pre-fills the dropdown correctly.
  "Commercial Mortgages",
  "Development Funding",
  "Bridging & Private Mortgages",
  // Secondary categories
  "Private Lender Exit",
  "Private Credit (Non-Bank)",
  "Business & Cash-flow",
  "SMSF & Asset Finance",
  "Commercial",
  "Business",
  "SMSF",
  "Asset & Equipment",
  "Refinance",
  "Other",
];

// Major Australian suburbs / cities with postcodes for autocomplete.
// Free-text entry is also allowed; users can type anything.
const AU_SUBURBS = [
  { name: "Sydney, NSW", postcode: "2000" },
  { name: "Melbourne, VIC", postcode: "3000" },
  { name: "Brisbane, QLD", postcode: "4000" },
  { name: "Perth, WA", postcode: "6000" },
  { name: "Adelaide, SA", postcode: "5000" },
  { name: "Hobart, TAS", postcode: "7000" },
  { name: "Darwin, NT", postcode: "0800" },
  { name: "Canberra, ACT", postcode: "2600" },
  { name: "Parramatta, NSW", postcode: "2150" },
  { name: "Bondi, NSW", postcode: "2026" },
  { name: "Surry Hills, NSW", postcode: "2010" },
  { name: "North Sydney, NSW", postcode: "2060" },
  { name: "Chatswood, NSW", postcode: "2067" },
  { name: "Manly, NSW", postcode: "2095" },
  { name: "Cronulla, NSW", postcode: "2230" },
  { name: "Hurstville, NSW", postcode: "2220" },
  { name: "Liverpool, NSW", postcode: "2170" },
  { name: "Penrith, NSW", postcode: "2750" },
  { name: "Newcastle, NSW", postcode: "2300" },
  { name: "Wollongong, NSW", postcode: "2500" },
  { name: "South Yarra, VIC", postcode: "3141" },
  { name: "St Kilda, VIC", postcode: "3182" },
  { name: "Brighton, VIC", postcode: "3186" },
  { name: "Toorak, VIC", postcode: "3142" },
  { name: "Carlton, VIC", postcode: "3053" },
  { name: "Fitzroy, VIC", postcode: "3065" },
  { name: "Richmond, VIC", postcode: "3121" },
  { name: "Geelong, VIC", postcode: "3220" },
  { name: "Ballarat, VIC", postcode: "3350" },
  { name: "Bendigo, VIC", postcode: "3550" },
  { name: "Gold Coast, QLD", postcode: "4217" },
  { name: "Surfers Paradise, QLD", postcode: "4217" },
  { name: "Sunshine Coast, QLD", postcode: "4551" },
  { name: "Cairns, QLD", postcode: "4870" },
  { name: "Townsville, QLD", postcode: "4810" },
  { name: "Toowoomba, QLD", postcode: "4350" },
  { name: "Fremantle, WA", postcode: "6160" },
  { name: "Subiaco, WA", postcode: "6008" },
  { name: "Joondalup, WA", postcode: "6027" },
  { name: "Glenelg, SA", postcode: "5045" },
  { name: "North Adelaide, SA", postcode: "5006" },
  { name: "Launceston, TAS", postcode: "7250" },
];

const initialForm = {
  loan_amount: "",
  loan_type: "",
  description: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  location: "",
  postcode: "",
};

// Map any category / taxonomy label onto a value that exists in LOAN_TYPES
const mapLoanType = (t) => {
  if (!t) return "";
  if (LOAN_TYPES.includes(t)) return t;
  const s = t.toLowerCase();
  if (/exit|caveat|take-?out/.test(s)) return "Private Lender Exit";
  if (/bridg/.test(s)) return "Bridging & Private Mortgages";
  if (/private credit|non-bank|second mortgage/.test(s)) return "Private Credit (Non-Bank)";
  if (/construction|development/.test(s)) return "Development Funding";
  if (/commercial|industrial|retail|office|medical|hospitality/.test(s)) return "Commercial Mortgages";
  if (/business|working capital|cash/.test(s)) return "Business & Cash-flow";
  if (/smsf|equipment|truck|car|asset/.test(s)) return "SMSF & Asset Finance";
  if (/refinanc|home|low-doc|low doc/.test(s)) return "Refinance";
  return "Other";
};

// Format integer string as AUD currency with leading $ and thousand separators
const formatAUD = (raw) => {
  const digits = (raw || "").replace(/[^\d]/g, "");
  if (!digits) return "";
  // Avoid leading zeros e.g. "007" -> 7
  const n = parseInt(digits, 10);
  if (Number.isNaN(n)) return "";
  return "$" + n.toLocaleString("en-AU");
};

// Accept and normalise Australian phone numbers (iPhone autofill commonly uses +61 format).
// Examples that all canonicalise to "0426976876":
//   "0426976876", "0426 976 876", "+61426976876", "+61 426 976 876", "61 426 976 876"
const normaliseAUPhone = (raw) => {
  if (!raw) return "";
  // Strip spaces, dashes, parentheses, dots — keep digits and leading +
  let v = raw.trim().replace(/[\s\-().]/g, "");
  if (v.startsWith("+61")) return "0" + v.slice(3);
  if (v.startsWith("0061")) return "0" + v.slice(4);
  if (v.startsWith("61") && v.length === 11) return "0" + v.slice(2);
  return v;
};

export default function LeadModal({ open, onOpenChange, initialAmount = "", initialLoanType = "", initialDescription = "", initialLocation = "", sourceChannel = "", searchContext = null, framing = "", onSuccess }) {
  const human = framing === "human_review";
  const copy = human ? {
    t0: "Your human review", d0: "Confirm the essentials from your search. Step 1 of 2.",
    t1: "Where should we send or discuss your review?", d1: "An OzyLoans finance professional will review your search, requirements and objectives and contact you to discuss the options that may be worth pursuing. Step 2 of 2.",
    submit: "Request my review", success: "Thanks — a finance professional will review your search and be in touch.",
  } : {
    t0: "Tell us about your loan", d0: "Amount, type and a quick scenario. Step 1 of 2.",
    t1: "Where can we reach you?", d1: "An OzyLoans finance professional will review your enquiry and contact you to discuss the next steps. Step 2 of 2.",
    submit: "Request a callback", success: "Thanks — a specialist will reach out shortly.",
  };
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot — humans never see/fill this
  const [openedAt, setOpenedAt] = useState(0);

  useEffect(() => {
    if (open) {
      setForm({
        ...initialForm,
        loan_amount: initialAmount ? formatAUD(initialAmount) : "",
        loan_type: mapLoanType(initialLoanType),
        description: initialDescription || "",
        location: initialLocation || "",
      });
      setStep(0);
      setWebsite("");
      setOpenedAt(Date.now());
      if (typeof window !== "undefined" && window.gtag) window.gtag("event", "lead_form_started", { source_channel: sourceChannel || "form" });
    }
  }, [open, initialAmount, initialLoanType, initialDescription, initialLocation, sourceChannel]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ---------------- Cross-browser autofill sync ----------------
  // Chrome/Safari/Firefox and password managers sometimes fill inputs without
  // dispatching a React-visible change event. This effect polls the raw DOM
  // values for the personal-details fields at 300ms/700ms/1500ms after step 1
  // opens and syncs any missed values back into React state. It's cheap, and
  // eliminates the class of bug where autofilled values look present visually
  // but are missing from the submitted payload.
  useEffect(() => {
    if (!open || step !== 1) return;
    const ids = {
      first_name: "lead-first-name",
      last_name:  "lead-last-name",
      email:      "lead-email",
      phone:      "lead-phone",
      location:   "lead-location",
      postcode:   "lead-postcode",
    };
    const sync = () => {
      setForm((f) => {
        const next = { ...f };
        let changed = false;
        for (const [key, id] of Object.entries(ids)) {
          const el = document.getElementById(id);
          const v = el && el.value;
          if (v && v !== f[key]) {
            next[key] = key === "phone" ? normaliseAUPhone(v) : v;
            changed = true;
          }
        }
        return changed ? next : f;
      });
    };
    const timers = [setTimeout(sync, 300), setTimeout(sync, 700), setTimeout(sync, 1500)];
    return () => timers.forEach(clearTimeout);
  }, [open, step]);

  const onAmountChange = (e) => {
    set("loan_amount", formatAUD(e.target.value));
  };

  const next = () => {
    if (step === 0) {
      if (!form.loan_amount) return toast.error("Please enter how much you'd like to borrow.");
      if (!form.loan_type) return toast.error("Please choose a loan type.");
    }
    setStep((s) => Math.min(s + 1, 1));
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name) return toast.error("Please enter your first and last name.");
    if (!form.email) return toast.error("Please enter your email.");
    if (!form.phone) return toast.error("Please enter your phone number.");

    setSubmitting(true);
    try {
      const fullName = `${form.first_name} ${form.last_name}`.trim();
      const normalisedPhone = normaliseAUPhone(form.phone);
      // Env-gated: empty string when RECAPTCHA_SITE_KEY not set — backend
      // then falls through to honeypot + dwell heuristics only.
      const recaptcha_token = RECAPTCHA_ENABLED ? await getRecaptchaToken("lead_submit") : "";
      await axios.post(`${API}/leads`, {
        // Combined name for backward compat with admin/email templates
        name: fullName,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: normalisedPhone,
        loan_amount: form.loan_amount,
        loan_type: form.loan_type,
        description: form.description,
        location: form.location,
        postcode: form.postcode,
        source_channel: sourceChannel || inferSourceChannel(),
        attribution: buildAttribution(searchContext || {}),
        website,
        dwell_ms: openedAt ? Date.now() - openedAt : 0,
        recaptcha_token,
      });
      try { localStorage.setItem("ozy_lead_submitted", "1"); } catch (e) { /* noop */ }
      if (typeof window !== "undefined") {
        if (window.gtag) window.gtag("event", "generate_lead", { loan_type: form.loan_type || "form", currency: "AUD" });
        if (window.gtag) window.gtag("event", "lead_form_submitted", { source_channel: sourceChannel || inferSourceChannel() });
        if (window.fbq) window.fbq("track", "Lead", { content_category: form.loan_type || "form", currency: "AUD" });
      }
      toast.success(copy.success);
      onOpenChange(false);
      setForm(initialForm);
      setStep(0);
      // Clear parent-owned pre-fill values (amount / loan type / description)
      // so the homepage input and modal both start blank on the next enquiry.
      // Only fires on successful submit — Step 0 ↔ Step 1 navigation is
      // unaffected, and browser autofill for the next visit is unchanged.
      if (typeof onSuccess === "function") onSuccess();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Validation error.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="lead-capture-modal"
        className="bg-[#0A0A0A] border border-zinc-800 rounded-2xl p-0 sm:max-w-xl text-white max-h-[92vh] overflow-y-auto"
      >
        <div className="p-6 sm:p-8">
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-6">
            {[0, 1].map((i) => (
              <div
                key={i}
                data-testid={`step-indicator-${i}`}
                className={`h-1 flex-1 rounded-full transition-all ${i <= step ? "bg-[#E50914]" : "bg-zinc-800"}`}
              />
            ))}
          </div>

          <DialogHeader className="space-y-2 text-left">
            <DialogTitle className="font-bebas text-3xl sm:text-4xl text-white tracking-wide" data-testid="lead-modal-title">
              {step === 0 && copy.t0}
              {step === 1 && copy.t1}
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm" data-testid="lead-modal-description">
              {step === 0 && copy.d0}
              {step === 1 && copy.d1}
            </DialogDescription>
          </DialogHeader>

          {/* STEP 0 — Loan details */}
          {step === 0 && (
            <div className="mt-6 space-y-4" data-testid="step-loan-details">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label htmlFor="lead-amount" className="text-xs uppercase tracking-wider text-zinc-500">Loan amount</label>
                  <Input
                    id="lead-amount"
                    data-testid="lead-amount-input"
                    inputMode="numeric"
                    value={form.loan_amount}
                    onChange={onAmountChange}
                    placeholder="$500,000"
                    className="bg-[#111] border-zinc-800 text-white placeholder:text-zinc-400 focus-visible:ring-[#E50914] focus-visible:border-[#E50914] h-11"
                    autoFocus
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="lead-loan-type" className="text-xs uppercase tracking-wider text-zinc-500">Loan type</label>
                  <Select value={form.loan_type} onValueChange={(v) => set("loan_type", v)}>
                    <SelectTrigger
                      id="lead-loan-type"
                      data-testid="lead-loan-type-select"
                      className="bg-[#111] border-zinc-800 text-white h-11 focus:ring-[#E50914] focus:border-[#E50914] data-[placeholder]:text-zinc-400"
                    >
                      <SelectValue placeholder="Select loan type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-zinc-800 text-white">
                      {LOAN_TYPES.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`loan-type-option-${t.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="lead-description" className="text-xs uppercase tracking-wider text-zinc-500">Loan requirements</label>
                <Textarea
                  id="lead-description"
                  data-testid="lead-description-input"
                  value={form.description}
                  onChange={update("description")}
                  placeholder="Tell us about your scenario, security, and timeline..."
                  rows={4}
                  className="bg-[#111] border-zinc-800 text-white placeholder:text-zinc-400 focus-visible:ring-[#E50914] focus-visible:border-[#E50914] resize-y min-h-[120px]"
                />
              </div>
            </div>
          )}

          {/* STEP 1 — Personal details */}
          {step === 1 && (
            <form onSubmit={handleSubmit} data-testid="step-contact" className="mt-6 space-y-4">
              {/* Honeypot */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", opacity: 0 }}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label htmlFor="lead-first-name" className="text-xs uppercase tracking-wider text-zinc-500">First name</label>
                  <Input
                    id="lead-first-name"
                    data-testid="lead-first-name-input"
                    value={form.first_name}
                    onChange={update("first_name")}
                    placeholder="John"
                    autoComplete="given-name"
                    name="given-name"
                    className="bg-[#111] border-zinc-800 text-white placeholder:text-zinc-400 focus-visible:ring-[#E50914] focus-visible:border-[#E50914] h-11"
                    autoFocus
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="lead-last-name" className="text-xs uppercase tracking-wider text-zinc-500">Last name</label>
                  <Input
                    id="lead-last-name"
                    data-testid="lead-last-name-input"
                    value={form.last_name}
                    onChange={update("last_name")}
                    placeholder="Smith"
                    autoComplete="family-name"
                    name="family-name"
                    className="bg-[#111] border-zinc-800 text-white placeholder:text-zinc-400 focus-visible:ring-[#E50914] focus-visible:border-[#E50914] h-11"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="lead-email" className="text-xs uppercase tracking-wider text-zinc-500">Email</label>
                <Input
                  id="lead-email"
                  data-testid="lead-email-input"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="john.smith@email.com"
                  autoComplete="email"
                  name="email"
                  className="bg-[#111] border-zinc-800 text-white placeholder:text-zinc-400 focus-visible:ring-[#E50914] focus-visible:border-[#E50914] h-11"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_120px] gap-3">
                <div className="space-y-1.5">
                  <label htmlFor="lead-phone" className="text-xs uppercase tracking-wider text-zinc-500">Phone</label>
                  <Input
                    id="lead-phone"
                    data-testid="lead-phone-input"
                    type="tel"
                    inputMode="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    onBlur={() => set("phone", normaliseAUPhone(form.phone))}
                    placeholder="0412 345 678 or +61 412 345 678"
                    autoComplete="tel"
                    name="tel"
                    className="bg-[#111] border-zinc-800 text-white placeholder:text-zinc-400 focus-visible:ring-[#E50914] focus-visible:border-[#E50914] h-11"
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="lead-location" className="text-xs uppercase tracking-wider text-zinc-500">Suburb / City</label>
                  <Input
                    id="lead-location"
                    data-testid="lead-location-input"
                    value={form.location}
                    onChange={(e) => {
                      const v = e.target.value;
                      // Auto-fill postcode if value matches a known suburb and postcode is empty
                      const match = AU_SUBURBS.find((s) => s.name.toLowerCase() === v.toLowerCase());
                      setForm((f) => ({
                        ...f,
                        location: v,
                        postcode: match && !f.postcode ? match.postcode : f.postcode,
                      }));
                    }}
                    placeholder="Start typing — e.g. Melbourne, VIC"
                    autoComplete="address-level2"
                    name="address-level2"
                    list="au-suburbs"
                    className="bg-[#111] border-zinc-800 text-white placeholder:text-zinc-400 focus-visible:ring-[#E50914] focus-visible:border-[#E50914] h-11"
                  />
                  <datalist id="au-suburbs">
                    {AU_SUBURBS.map((s) => (
                      <option key={s.name} value={s.name}>{s.postcode}</option>
                    ))}
                  </datalist>
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="lead-postcode" className="text-xs uppercase tracking-wider text-zinc-500">Postcode</label>
                  <Input
                    id="lead-postcode"
                    data-testid="lead-postcode-input"
                    type="text"
                    inputMode="numeric"
                    pattern="\d{4}"
                    maxLength={4}
                    value={form.postcode}
                    onChange={(e) => set("postcode", e.target.value.replace(/\D/g, "").slice(0, 4))}
                    placeholder="3000"
                    autoComplete="postal-code"
                    name="postal-code"
                    className="bg-[#111] border-zinc-800 text-white placeholder:text-zinc-400 focus-visible:ring-[#E50914] focus-visible:border-[#E50914] h-11"
                  />
                </div>
              </div>

              {/* Recap */}
              <div className="rounded-xl border border-zinc-800 bg-[#0c0c0c] p-3 text-xs text-zinc-400">
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <span><span className="text-zinc-600">Amount:</span> <span className="text-white">{form.loan_amount || "—"}</span></span>
                  <span><span className="text-zinc-600">Type:</span> <span className="text-white">{form.loan_type || "—"}</span></span>
                </div>
              </div>

              <p className="text-[11px] text-zinc-600 text-center pt-1">
                By submitting this enquiry, you consent to OzyLoans collecting and using your information to assess your scenario, and contacting you by phone, email or SMS regarding your finance request. We may receive commissions from lenders we introduce you to — see our <a href="/credit-guide" target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-[#E50914] underline">Credit Guide</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-[#E50914] underline">Privacy Policy</a> for more information.
              </p>
              {RECAPTCHA_ENABLED && (
                <p className="text-[10px] text-zinc-700 text-center leading-relaxed">
                  Protected by reCAPTCHA. Google&apos;s <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-[#E50914] underline">Privacy Policy</a> and <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-zinc-600 hover:text-[#E50914] underline">Terms of Service</a> apply.
                </p>
              )}

              {/* Hidden submit button — lets Enter key submit the form from any input */}
              <button type="submit" aria-hidden="true" tabIndex={-1} className="sr-only">Submit</button>
            </form>
          )}

          {/* Footer nav */}
          <div className="flex items-center justify-between gap-3 mt-6">
            {step > 0 ? (
              <Button
                type="button"
                onClick={back}
                variant="ghost"
                data-testid="step-back-button"
                className="text-zinc-400 hover:text-white hover:bg-[#111] rounded-full px-4 h-11"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            ) : (
              <span />
            )}

            {step < 1 ? (
              <Button
                type="button"
                onClick={next}
                data-testid="step-next-button"
                className="bg-[#E50914] hover:bg-[#F6121D] text-white font-semibold rounded-full px-6 h-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0A0A]"
              >
                Continue <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="lead-submit-button"
                className="bg-[#E50914] hover:bg-[#F6121D] text-white font-semibold rounded-full px-6 h-11 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0A0A]"
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    {copy.submit} <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
